import React, { useState, useEffect } from 'react';
import { FaBook, FaPlus, FaTrash, FaFileUpload, FaTimes, FaMoneyBillWave, FaCreditCard, FaFileContract, FaCheckCircle, FaCalendarAlt, FaUserTie } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as profileService from '../services/profileService';
import * as masterService from '../services/masterService';

const SubmitPaper = () => {
  const navigate = useNavigate();

  // 1. All State Declarations Moved to Top to avoid TDZ
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [acceptedAbstracts, setAcceptedAbstracts] = useState([]);
  const [selectedAbstractId, setSelectedAbstractId] = useState('');
  const [isAbstractSelected, setIsAbstractSelected] = useState(false);
  const [universityList, setUniversityList] = useState([]);
  const [searchUniversity, setSearchUniversity] = useState('');
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [searchMentorUniversity, setSearchMentorUniversity] = useState('');
  const [showMentorUniversityDropdown, setShowMentorUniversityDropdown] = useState(false);

  const [formData, setFormData] = useState({
    subject: '',
    major: '',
    specialization: '',
    university: '',
    paperTitle: '',
    linkedAbstractId: '',
    mentorName: '',
    mentorDesignation: '',
    mentorUniversity: '',
    mentorPhone: '',
    mentorEmail: '',
    submissionDate: new Date().toISOString().split('T')[0],
    keywords: '',
  });

  const [authors, setAuthors] = useState([
    {
      id: 1,
      name: '',
      email: '',
      university: '',
      searchUniversity: '',
      showUniversityDropdown: false,
    },
  ]);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'upi'

  // Payment form state
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: '',
    upiId: '',
  });

  // Subjects and Majors
  const subjects = [
    'Computer Science',
    'Electronics',
    'Mechanical Engineering',
    'Civil Engineering',
    'Electrical Engineering',
    'Physics',
    'Chemistry',
    'Mathematics',
    'Biology',
    'Commerce',
    'Arts',
    'Other',
  ];

  const majorsBySubject = {
    'Computer Science': ['Software Engineering', 'Artificial Intelligence', 'Data Science', 'Cyber Security', 'Web Development'],
    'Electronics': ['VLSI Design', 'Embedded Systems', 'Communication Systems', 'Signal Processing'],
    'Mechanical Engineering': ['Thermal Engineering', 'Manufacturing', 'Automotive', 'Robotics'],
    'Physics': ['Quantum Physics', 'Nuclear Physics', 'Astrophysics', 'Applied Physics'],
    'Chemistry': ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Analytical Chemistry'],
    'Mathematics': ['Pure Mathematics', 'Applied Mathematics', 'Statistics', 'Operations Research'],
  };

  // 2. Effects
  // Check if user is logged in, load accepted abstracts and profile data
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadInitialData = async () => {
      try {
        const safeGetProfile = async () => {
          try {
            return await profileService.getProfile();
          } catch (e) {
            console.error('Profile fetch failed:', e);
            return null;
          }
        };

        const [univRes, profileData] = await Promise.all([
          masterService.getAllUniversities(),
          safeGetProfile()
        ]);

        let univArray = [];
        if (univRes.success && Array.isArray(univRes.data)) {
          univArray = univRes.data;
        } else if (Array.isArray(univRes)) {
          univArray = univRes;
        }
        setUniversityList(univArray.slice(0, 50));

        // Auto-fill university from profile if available
        if (profileData?.success && profileData?.data?.academic?.university) {
          const userUniversity = profileData.data.academic.university;
          setFormData(prev => ({
            ...prev,
            university: userUniversity
          }));
          setSearchUniversity(userUniversity);
        }

        // Load accepted abstracts for the user - ONLY those without linked papers
        const allAbstracts = JSON.parse(localStorage.getItem('abstracts') || '[]');
        const allPapers = JSON.parse(localStorage.getItem('papers') || '[]');

        // Get IDs of abstracts already linked to papers
        const linkedAbstractIds = allPapers
          .filter(paper => paper.linkedAbstractId)
          .map(paper => paper.linkedAbstractId.toString());

        // Filter out abstracts that are already linked to papers
        const userAcceptedAbstracts = allAbstracts.filter(
          abstract =>
            abstract.userId === currentUser.id &&
            abstract.status === 'accepted' &&
            !linkedAbstractIds.includes(abstract.id.toString())
        );

        setAcceptedAbstracts(userAcceptedAbstracts);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadInitialData();
  }, [navigate]);

  // Debounced university search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchUniversity && searchUniversity.length >= 2) {
        if (formData.university === searchUniversity) return;
        try {
          const response = await masterService.searchUniversities(searchUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }
          if (!results.some(u => (u.name || u) === "Other")) {
            results.push({ name: "Other", state: "" });
          }
          setUniversityList(results);
          setShowUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchUniversity, formData.university]);

  // Debounced mentor university search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchMentorUniversity && searchMentorUniversity.length >= 2) {
        if (formData.mentorUniversity === searchMentorUniversity) return;
        try {
          const response = await masterService.searchUniversities(searchMentorUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }
          if (!results.some(u => (u.name || u) === "Other")) {
            results.push({ name: "Other", state: "" });
          }
          setUniversityList(results);
          setShowMentorUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchMentorUniversity, formData.mentorUniversity]);


  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleAbstractSelection = (abstractId) => {
    setSelectedAbstractId(abstractId);

    if (!abstractId) {
      // Reset form if no abstract selected
      setFormData({
        subject: '',
        major: '',
        specialization: '',
        university: '',
        paperTitle: '',
        linkedAbstractId: '',
        mentorName: '',
        mentorDesignation: '',
        mentorUniversity: '',
        mentorPhone: '',
        mentorEmail: '',
        submissionDate: new Date().toISOString().split('T')[0],
        keywords: '',
      });
      setAuthors([{ id: 1, name: '', email: '', university: '' }]);
      setIsAbstractSelected(false);
      return;
    }

    const selectedAbstract = acceptedAbstracts.find(abs => abs.id.toString() === abstractId);

    if (selectedAbstract) {
      // Auto-fill form with abstract data
      setFormData({
        subject: selectedAbstract.subject || '',
        major: selectedAbstract.major || '',
        specialization: selectedAbstract.specialization || '',
        university: selectedAbstract.university || '',
        paperTitle: selectedAbstract.abstractTitle || '',
        linkedAbstractId: abstractId,
        mentorName: selectedAbstract.mentorName || selectedAbstract.guideName || '',
        mentorDesignation: selectedAbstract.mentorDesignation || selectedAbstract.guideDesignation || '',
        mentorUniversity: selectedAbstract.mentorUniversity || selectedAbstract.guideUniversity || '',
        mentorPhone: selectedAbstract.mentorPhone || selectedAbstract.guidePhone || '',
        mentorEmail: selectedAbstract.mentorEmail || selectedAbstract.guideEmail || '',
        submissionDate: new Date().toISOString().split('T')[0],
        keywords: selectedAbstract.keywords || '',
      });

      // Update search fields as well
      setSearchUniversity(selectedAbstract.university || '');
      setSearchMentorUniversity(selectedAbstract.mentorUniversity || selectedAbstract.guideUniversity || '');

      // Auto-fill authors
      if (selectedAbstract.authors && selectedAbstract.authors.length > 0) {
        setAuthors(selectedAbstract.authors.map((author, index) => ({
          id: index + 1,
          name: author.name || '',
          email: author.email || '',
          university: author.university || '',
          searchUniversity: author.university || '',
          showUniversityDropdown: false,
        })));
      }

      setIsAbstractSelected(true);
      showToast('Abstract details loaded successfully! Fields are now locked.', 'success');
    }
  };

  const handleChange = (e) => {
    if (isAbstractSelected) {
      return; // Prevent changes if abstract is selected
    }
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({ ...paymentData, [name]: value });
  };

  const handleAuthorChange = (id, field, value) => {
    if (isAbstractSelected) {
      return; // Prevent changes if abstract is selected
    }
    setAuthors(prevAuthors =>
      prevAuthors.map((author) =>
        author.id === id ? { ...author, [field]: value } : author
      )
    );

    // If searching university in authors section, trigger global university search
    if (field === 'searchUniversity' && value.length >= 2) {
      const fetchUnivs = async () => {
        try {
          const response = await masterService.searchUniversities(value);
          if (response.success && Array.isArray(response.data)) {
            setUniversityList(response.data);
          } else if (Array.isArray(response)) {
            setUniversityList(response);
          }
        } catch (err) {
          console.error('Author university search failed:', err);
        }
      };
      fetchUnivs();
    }
  };

  const addAuthor = () => {
    if (isAbstractSelected) {
      showToast('Cannot add authors when abstract is selected', 'error');
      return;
    }
    const newId = Math.max(...authors.map((a) => a.id)) + 1;
    setAuthors([
      ...authors,
      {
        id: newId,
        name: '',
        email: '',
        university: '',
      },
    ]);
  };

  const removeAuthor = (id) => {
    if (isAbstractSelected) {
      showToast('Cannot remove authors when abstract is selected', 'error');
      return;
    }
    if (authors.length > 1) {
      setAuthors(authors.filter((author) => author.id !== id));
    } else {
      showToast('At least one author is required', 'error');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setUploadedFile(file);
      } else {
        showToast('Please upload only PDF or Word documents', 'error');
        e.target.value = '';
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
  };

  const validateForm = () => {
    if (!formData.subject || !formData.major || !formData.paperTitle) {
      showToast('Please fill in all required fields', 'error');
      return false;
    }

    // Authors are optional as per user request

    if (!uploadedFile) {
      showToast('Please upload a paper document', 'error');
      return false;
    }

    return true;
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Bypassing payment as per request
      // setShowPaymentModal(true);
      submitPaper();
    }
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();

    // Validate payment details
    if (paymentMethod === 'card') {
      if (!paymentData.cardNumber || !paymentData.cardHolder || !paymentData.expiryDate || !paymentData.cvv) {
        showToast('Please fill in all card details', 'error');
        return;
      }
      if (paymentData.cardNumber.replace(/\s/g, '').length !== 16) {
        showToast('Please enter a valid 16-digit card number', 'error');
        return;
      }
      if (paymentData.cvv.length !== 3) {
        showToast('Please enter a valid 3-digit CVV', 'error');
        return;
      }
    } else if (paymentMethod === 'upi') {
      if (!paymentData.upiId) {
        showToast('Please enter your UPI ID', 'error');
        return;
      }
    }

    // Simulate payment processing
    setIsSubmitting(true);
    setTimeout(() => {
      setPaymentCompleted(true);
      showToast('Payment successful! ₹500 paid', 'success');
      setShowPaymentModal(false);
      setIsSubmitting(false);

      // Auto submit after payment
      setTimeout(() => {
        submitPaper();
      }, 1000);
    }, 2000);
  };

  const submitPaper = () => {
    setIsSubmitting(true);

    try {
      const currentUser = authService.getCurrentUser();

      const paper = {
        id: Date.now(),
        userId: currentUser.id,
        userName: currentUser.name,
        ...formData,
        linkedAbstractId: formData.linkedAbstractId || null,
        authors: authors.filter(a => a.name || a.email || a.university || a.searchUniversity).map(a => ({
          ...a,
          university: a.university || a.searchUniversity
        })),
        uploadedFile: {
          name: uploadedFile.name,
          type: uploadedFile.type,
          size: uploadedFile.size
        },
        contentType: 'file',
        status: 'submitted',
        paymentAmount: 500,
        paymentMethod: paymentMethod,
        paymentDate: new Date().toISOString(),
        submittedAt: new Date().toISOString(),
      };

      const existingPapers = JSON.parse(localStorage.getItem('papers') || '[]');
      existingPapers.push(paper);
      localStorage.setItem('papers', JSON.stringify(existingPapers));

      // Store payment record
      const payments = JSON.parse(localStorage.getItem('payments') || '[]');
      payments.push({
        id: Date.now(),
        userId: currentUser.id,
        type: 'paper',
        paperId: paper.id,
        amount: 500,
        paymentMethod: paymentMethod,
        status: 'completed',
        date: new Date().toISOString(),
      });
      localStorage.setItem('payments', JSON.stringify(payments));

      showToast('Paper submitted successfully!', 'success');

      setTimeout(() => {
        setFormData({
          subject: '',
          major: '',
          specialization: '',
          university: '',
          paperTitle: '',
          linkedAbstractId: '',
          mentorName: '',
          mentorDesignation: '',
          mentorUniversity: '',
          mentorPhone: '',
          mentorEmail: '',
          submissionDate: new Date().toISOString().split('T')[0],
          keywords: '',
        });
        setAuthors([{ id: 1, name: '', email: '', university: '' }]);
        setUploadedFile(null);
        setSelectedAbstractId('');
        setIsAbstractSelected(false);
        setPaymentCompleted(false);
        setPaymentData({
          cardNumber: '',
          cardHolder: '',
          expiryDate: '',
          cvv: '',
          upiId: '',
        });
        setIsSubmitting(false);

        // Reload accepted abstracts (to exclude the newly linked one)
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error submitting paper:', error);
      showToast('Error submitting paper. Please try again.', 'error');
      setIsSubmitting(false);
    }
  };

  const inputClass = `w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all hover:border-gray-300 text-sm ${isAbstractSelected ? 'bg-gray-100 cursor-not-allowed' : ''}`;
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";
  const sectionBadgeClass = "w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold shadow-lg text-white";

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Payment Modal - Disabled as per request
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
          ... (omitted content for brevity) ...
        </div>
      )}
      */}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="rounded-2xl shadow-lg p-6 mb-6 relative overflow-hidden" style={darkGradient}>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Submit Paper for Review</h1>
                <p className="text-blue-100 text-sm">Share your research paper with the community</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaBook className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Accepted Abstracts Table - Only if abstracts exist */}
          {acceptedAbstracts.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6">
                <div className="flex items-center gap-3">
                  <FaCheckCircle className="text-3xl text-white" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Your Accepted Abstracts</h2>
                    <p className="text-green-100">Select an abstract to auto-fill the paper submission form (fields will be locked)</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Select</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Abstract Title</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Subject</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Major</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Specialization</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Accepted Date</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Professor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {acceptedAbstracts.map((abstract) => (
                        <tr
                          key={abstract.id}
                          className={`hover:bg-blue-50 transition-colors ${selectedAbstractId === abstract.id.toString() ? 'bg-blue-100' : ''}`}
                        >
                          <td className="px-4 py-4">
                            <input
                              type="radio"
                              name="selectedAbstract"
                              value={abstract.id}
                              checked={selectedAbstractId === abstract.id.toString()}
                              onChange={(e) => handleAbstractSelection(e.target.value)}
                              className="w-5 h-5 text-primary-blue focus:ring-primary-blue cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm font-semibold text-gray-900 line-clamp-2">{abstract.abstractTitle}</p>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm font-medium text-gray-900">{abstract.subject}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm font-medium text-gray-900">{abstract.major}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-600">{abstract.specialization || 'N/A'}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1 text-sm">
                              <FaCalendarAlt className="text-green-500 text-xs" />
                              <span className="font-medium text-gray-900">{formatDate(abstract.acceptedAt)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1 text-sm">
                              <FaUserTie className="text-indigo-500 text-xs" />
                              <span className="font-medium text-gray-900">{abstract.assignedTo?.professorName || 'N/A'}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {selectedAbstractId && (
                  <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-blue-600" />
                      <p className="text-sm font-semibold text-blue-900">
                        Abstract selected! Form fields have been auto-filled and locked. You cannot edit them.
                      </p>
                    </div>
                  </div>
                )}

                {!selectedAbstractId && (
                  <div className="mt-4 p-4 bg-gray-50 border-2 border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <strong>💡 Tip:</strong> Select an abstract to automatically fill the form (fields will be locked), or fill the form manually below.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <form onSubmit={handleProceedToPayment}>

              {/* 1. Paper Details - MOVED TO TOP */}
              <div className="p-6 bg-gradient-to-br from-purple-50/30 to-pink-50/30 border-b-2 border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>1</div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Paper Details</h2>
                    <p className="text-gray-500 text-sm">Provide information about your paper</p>
                  </div>
                </div>

                <div className="mb-4">
                  <label className={labelClass}>Paper Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="paperTitle"
                    value={formData.paperTitle}
                    onChange={handleChange}
                    required
                    disabled={isAbstractSelected}
                    className={inputClass}
                    placeholder="Enter the title of your research paper"
                  />
                </div>

                <div>
                  <label className={labelClass}>Submission Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="submissionDate"
                    value={formData.submissionDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all hover:border-gray-300 text-sm"
                  />
                </div>
              </div>

              {/* 2. Linked Abstract Info - Show if selected */}
              {selectedAbstractId && (
                <div className="p-6 bg-gradient-to-br from-indigo-50/30 to-purple-50/30 border-b-2 border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`${sectionBadgeClass}`} style={darkGradient}>2</div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Linked Abstract</h2>
                      <p className="text-gray-500 text-sm">This paper is based on your accepted abstract</p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border-2 border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FaFileContract className="text-indigo-600" />
                      <p className="font-bold text-gray-900">
                        {acceptedAbstracts.find(abs => abs.id.toString() === selectedAbstractId)?.abstractTitle}
                      </p>
                    </div>
                    <p className="text-sm text-red-600 font-semibold">
                      ⚠️ All details from this abstract have been pre-filled and locked. You cannot edit them.
                    </p>
                  </div>
                </div>
              )}

              {/* 3. Academic Information */}
              <div className="p-6 border-b-2 border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>
                    {selectedAbstractId ? '3' : '2'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Academic Information</h2>
                    <p className="text-gray-500 text-sm">Select your field of study</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className={labelClass}>Subject <span className="text-red-500">*</span></label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={isAbstractSelected}
                      className={`${inputClass} bg-white`}
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Major <span className="text-red-500">*</span></label>
                    <select
                      name="major"
                      value={formData.major}
                      onChange={handleChange}
                      required
                      disabled={isAbstractSelected || !formData.subject || !majorsBySubject[formData.subject]}
                      className={`${inputClass} bg-white disabled:bg-gray-50`}
                    >
                      <option value="">Select Major</option>
                      {formData.subject && majorsBySubject[formData.subject]?.map((maj) => (
                        <option key={maj} value={maj}>{maj}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Specialization</label>
                    <input
                      type="text"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      disabled={isAbstractSelected}
                      className={inputClass}
                      placeholder="Your specialization (optional)"
                    />
                  </div>

                  {/* University - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>University <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Search & Select University"
                      value={formData.university || searchUniversity}
                      onChange={(e) => {
                        setSearchUniversity(e.target.value);
                        setFormData({ ...formData, university: '' });
                        setShowUniversityDropdown(true);
                      }}
                      onFocus={() => setShowUniversityDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUniversityDropdown(false), 200)}
                      className={`${inputClass} bg-white`}
                      disabled={isAbstractSelected}
                      required
                    />
                    {showUniversityDropdown && !isAbstractSelected && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {universityList.length > 0 ? (
                          universityList.map((uni, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                              onMouseDown={() => {
                                const name = uni.name || uni;
                                setFormData({ ...formData, university: name });
                                setSearchUniversity(name);
                                setShowUniversityDropdown(false);
                              }}
                            >
                              {uni.name || uni}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                            onMouseDown={() => {
                              setFormData({ ...formData, university: 'Other' });
                              setSearchUniversity('Other');
                              setShowUniversityDropdown(false);
                            }}
                          >
                            Other (Type manually)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Authors Information */}
              <div className="p-6 border-b-2 border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>
                    {selectedAbstractId ? '4' : '3'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Authors Information</h2>
                    <p className="text-gray-500 text-sm">Add all contributing authors</p>
                  </div>
                </div>

                {authors.map((author, index) => (
                  <div key={author.id} className="mb-4 p-4 bg-green-50/50 rounded-xl border-l-4 border-green-500">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <div className="w-7 h-7 bg-green-500 text-white rounded-lg flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </div>
                        Author {index + 1}
                      </h3>
                      {authors.length > 1 && !isAbstractSelected && (
                        <button
                          type="button"
                          onClick={() => removeAuthor(author.id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={labelClass}>Author Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={author.name}
                          onChange={(e) => handleAuthorChange(author.id, 'name', e.target.value)}
                          required
                          disabled={isAbstractSelected}
                          className={inputClass}
                          placeholder="Full name"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Author Email <span className="text-red-500">*</span></label>
                        <input
                          type="email"
                          value={author.email}
                          onChange={(e) => handleAuthorChange(author.id, 'email', e.target.value)}
                          required
                          disabled={isAbstractSelected}
                          className={inputClass}
                          placeholder="email@example.com"
                        />
                      </div>

                      {/* Author University - Searchable Dropdown */}
                      <div className="relative">
                        <label className={labelClass}>Author University <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          placeholder="Search & Select University"
                          value={author.university || author.searchUniversity || ''}
                          onChange={(e) => {
                            handleAuthorChange(author.id, 'searchUniversity', e.target.value);
                            handleAuthorChange(author.id, 'university', '');
                            handleAuthorChange(author.id, 'showUniversityDropdown', true);
                          }}
                          onFocus={() => handleAuthorChange(author.id, 'showUniversityDropdown', true)}
                          onBlur={() => setTimeout(() => handleAuthorChange(author.id, 'showUniversityDropdown', false), 200)}
                          className={`${inputClass} bg-white`}
                          disabled={isAbstractSelected}
                          required
                        />
                        {author.showUniversityDropdown && !isAbstractSelected && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {universityList.filter(u => {
                              const name = (u.name || u || '').toLowerCase();
                              const search = (author.searchUniversity || '').toLowerCase();
                              return !search || name.includes(search);
                            }).length > 0 ? (
                              universityList.filter(u => {
                                const name = (u.name || u || '').toLowerCase();
                                const search = (author.searchUniversity || '').toLowerCase();
                                return !search || name.includes(search);
                              }).map((uni, idx) => (
                                <div
                                  key={idx}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                  onMouseDown={() => {
                                    const name = uni.name || uni;
                                    handleAuthorChange(author.id, 'university', name);
                                    handleAuthorChange(author.id, 'searchUniversity', name);
                                    handleAuthorChange(author.id, 'showUniversityDropdown', false);
                                  }}
                                >
                                  {uni.name || uni}
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                                onMouseDown={() => {
                                  handleAuthorChange(author.id, 'university', 'Other');
                                  handleAuthorChange(author.id, 'searchUniversity', 'Other');
                                  handleAuthorChange(author.id, 'showUniversityDropdown', false);
                                }}
                              >
                                Other (Type manually)
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {!isAbstractSelected && (
                  <button
                    type="button"
                    onClick={addAuthor}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md font-semibold"
                  >
                    <FaPlus />
                    Add Another Author
                  </button>
                )}
              </div>

              {/* 5. Mentor Information */}
              <div className="p-6 bg-gradient-to-br from-orange-50/30 to-yellow-50/30 border-b-2 border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>
                    {selectedAbstractId ? '5' : '4'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Mentor Information</h2>
                    <p className="text-gray-500 text-sm">Research mentor details</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className={labelClass}>Mentor Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="mentorName"
                      value={formData.mentorName}
                      onChange={handleChange}
                      required
                      disabled={isAbstractSelected}
                      className={inputClass}
                      placeholder="Mentor's full name"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Designation <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="mentorDesignation"
                      value={formData.mentorDesignation}
                      onChange={handleChange}
                      required
                      disabled={isAbstractSelected}
                      className={inputClass}
                      placeholder="e.g., Professor"
                    />
                  </div>

                  {/* Mentor University - Searchable Dropdown */}
                  <div className="relative">
                    <label className={labelClass}>Mentor University <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      placeholder="Search & Select University"
                      value={formData.mentorUniversity || searchMentorUniversity}
                      onChange={(e) => {
                        setSearchMentorUniversity(e.target.value);
                        setFormData({ ...formData, mentorUniversity: '' });
                        setShowMentorUniversityDropdown(true);
                      }}
                      onFocus={() => setShowMentorUniversityDropdown(true)}
                      onBlur={() => setTimeout(() => setShowMentorUniversityDropdown(false), 200)}
                      className={`${inputClass} bg-white`}
                      disabled={isAbstractSelected}
                      required
                    />
                    {showMentorUniversityDropdown && !isAbstractSelected && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {universityList.length > 0 ? (
                          universityList.map((uni, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                              onMouseDown={() => {
                                const name = uni.name || uni;
                                setFormData({ ...formData, mentorUniversity: name });
                                setSearchMentorUniversity(name);
                                setShowMentorUniversityDropdown(false);
                              }}
                            >
                              {uni.name || uni}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm cursor-pointer hover:bg-blue-50"
                            onMouseDown={() => {
                              setFormData({ ...formData, mentorUniversity: 'Other' });
                              setSearchMentorUniversity('Other');
                              setShowMentorUniversityDropdown(false);
                            }}
                          >
                            Other (Type manually)
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={labelClass}>Mentor Phone <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      name="mentorPhone"
                      value={formData.mentorPhone}
                      onChange={handleChange}
                      required
                      disabled={isAbstractSelected}
                      maxLength="10"
                      className={inputClass}
                      placeholder="10-digit number"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Mentor Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="mentorEmail"
                      value={formData.mentorEmail}
                      onChange={handleChange}
                      required
                      disabled={isAbstractSelected}
                      className={inputClass}
                      placeholder="mentor@university.edu"
                    />
                  </div>
                </div>
              </div>

              {/* 6. Paper Document Upload - FILE ONLY */}
              <div className="p-6 border-b-2 border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`${sectionBadgeClass}`} style={darkGradient}>
                    {selectedAbstractId ? '6' : '5'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Paper Document</h2>
                    <p className="text-gray-500 text-sm">Upload your research paper document</p>
                  </div>
                </div>

                {/* File Upload */}
                {!uploadedFile && (
                  <div className="mb-6">
                    <label className={labelClass}>Upload Document (PDF or Word) <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        required
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100 cursor-pointer"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Supported formats: PDF, DOC, DOCX (Required)</p>
                  </div>
                )}

                {/* Display uploaded file with remove option */}
                {uploadedFile && (
                  <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                          <FaFileUpload className="text-white text-xl" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-600">
                            {(uploadedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                        title="Remove file"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                )}

                {/* Keywords */}
                <div>
                  <label className={labelClass}>Keywords <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    required
                    disabled={isAbstractSelected}
                    className={inputClass}
                    placeholder="e.g., machine learning, artificial intelligence, data science"
                  />
                  <p className="text-xs text-gray-500 mt-2">Separate keywords with commas</p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="p-6 flex justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex items-center gap-3 px-12 py-4 bg-gradient-to-r from-primary-blue via-blue-600 to-secondary-blue text-white text-lg font-bold rounded-xl hover:shadow-xl transform hover:scale-105 transition-all shadow-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed transform-none' : ''
                    }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaFileUpload />
                      Submit Paper
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitPaper;